export const formatMoney = (
  { amount, currencyCode = 'USD' },
  cents = false,
) => {
  if (Number.isNaN(+amount)) {
    // eslint-disable-next-line no-console
    return console.error('utils/format-money.js: No amount value passed.');
  }

  if (cents) amount /= 100;

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return formatter.format(amount);
};

export const getProductBadge = (productTags, identifier) => {
  if (!productTags || !productTags.length) return false;

  const badgeTag = productTags.find((tag) => tag.includes(identifier));

  return badgeTag ? badgeTag.split(identifier)[1] : null;
};

export const checkInventoryTreshold = (variant) => {
  const inventoryThreshold = window.Benchmade.inventoryThreshold
    ? Number(window.Benchmade.inventoryThreshold)
    : null;

  if (!inventoryThreshold) return false;

  return (
    inventoryThreshold > variant.quantityAvailable &&
    variant.quantityAvailable > 0
  );
};

export const getCookie = (cname) => {
  const name = `${cname}=`;
  const ca = document.cookie.split(';');
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1);
    if (c.indexOf(name) === 0) return c.substring(name.length, c.length);
  }
  return '';
};

export const isOnSale = (price, compareAtPrice) =>
  Number(price?.amount) < Number(compareAtPrice?.amount);

export const cartContainsGiftCard = (cartItems) => {
  if (!cartItems) return false;

  const containsGiftCard = cartItems.some((item) =>
    item.merchandise.product.productType.includes('Gift Card'),
  );

  return containsGiftCard;
};

export const cartContainsCustom = (cartItems) => {
  if (!cartItems) return false;

  const containsCustom = cartItems.some(
    (item) =>
      Object.keys(item.attributes).includes('_is_personalization') ||
      Object.keys(item.attributes).includes('_is_main') ||
      Object.keys(item.attributes).includes('_is_component'),
  );

  return containsCustom;
};

export const makeDivHorizontallyScrollable = (elementSelector) => {
  const slider = elementSelector;
  let isDown = false;
  let startX;
  let scrollLeft;

  slider.addEventListener('mousedown', (e) => {
    isDown = true;
    slider.classList.add('active');
    startX = e.pageX - slider.offsetLeft;
    scrollLeft = slider.scrollLeft;
  });

  slider.addEventListener('mouseleave', () => {
    isDown = false;
    slider.classList.remove('active');
  });

  slider.addEventListener('mouseup', () => {
    isDown = false;
    slider.classList.remove('active');
  });

  slider.addEventListener('mousemove', (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - slider.offsetLeft;
    const walk = (x - startX) * 3; // scroll-fast
    slider.scrollLeft = scrollLeft - walk;
  });
};

export const getUserProperties = (customerId) => {
  if (customerId) {
    return {
      user_id: customerId,
      visitor_type: 'logged_in',
    };
  }

  return {
    visitor_type: 'guest',
  };
};

export const elevarTracking = ({
  customerId,
  products = [],
  event,
  cartTotal,
}) => {
  window.ElevarDataLayer = window.ElevarDataLayer ?? [];

  const ecommerce = {
    currencyCode: window.Shopify?.currency?.active,
  };

  if (event === 'dl_view_cart') {
    ecommerce.actionField = {
      list: 'Shopping Cart',
    };
    ecommerce.impressions = products;
  }

  if (event === 'dl_add_to_cart') {
    ecommerce.add = {
      actionField: {
        list: window.location.pathname,
      },
      products,
    };
  }

  if (event === 'dl_remove_from_cart') {
    ecommerce.remove = {
      actionField: {
        list: window.location.pathname,
      },
      products,
    };
  }

  if (event === 'dl_view_search_results') {
    ecommerce.actionField = {
      list: 'search results',
    };
  }

  window.ElevarDataLayer.push({
    event,
    ...(cartTotal && { cart_total: cartTotal }),
    user_properties: getUserProperties(customerId),
    ecommerce,
  });
};

export const getProductsForCard = (products, showAllFamilyProducts = false) => {
  const familyTags = [
    ...new Set(
      products
        ?.map((product) =>
          product.tags.find((element) => element.includes('family')),
        )
        .filter((product) => product),
    ),
  ];

  const familyProducts = familyTags?.map((tag) => {
    const filteredProducts = products.filter((product) =>
      product.tags.includes(tag),
    );
    return { family: tag.split('family:')[1], products: filteredProducts };
  });

  const simpleProducts = products?.reduce((acc, product) => {
    if (
      !product.tags.length ||
      !product.tags.filter((tag) => familyTags?.includes(tag)).length
    ) {
      return [{ family: 'none', products: [product] }, ...acc];
    }
    return acc;
  }, []);

  if (showAllFamilyProducts) {
    const transformedProducts = familyProducts.flatMap((family) =>
      family.products.map((product, index) => {
        const updatedProducts = [
          product,
          ...family.products.filter((_, i) => i !== index),
        ];

        return {
          family: family.family,
          products: updatedProducts,
        };
      }),
    );
    return [...transformedProducts, ...simpleProducts];
  }

  return [...familyProducts, ...simpleProducts];
};

/**
 * Validate if cart consist some type of the envelope line items
 * @param {Array} cartItems
 * @param {String} envelopeType
 */
export const hasEnvelopeLineItem = (cartItems, envelopeType) => {
  return (cartItems || []).some((item) => {
    const attributes = item.attributes || [];
    const hasEnvelope = attributes.some(
      (attr) => attr.key === 'envelope' && attr.value === 'true',
    );
    const hasEnvelopeType = attributes.some(
      (attr) => attr.key === 'envelope_type' && attr.value === envelopeType,
    );

    return hasEnvelope && hasEnvelopeType;
  });
};

export const hasSheathProductLineItem = (cartItems) => {
  return (cartItems || []).some((item) => {
    const attributes = item.attributes || [];
    const hasSheath = attributes.some(
      (attr) => attr.key === 'sheath' && attr.value === 'true',
    );
    return hasSheath;
  });
};

export const parseKnifeTypeFromTag = (tag) => {
  if (!tag) return 'unknown';
  if (tag === 'service:fixed:1') return 'fixed';
  if (tag === 'service:folder:1') return 'folder';
  if (tag === 'service:cutlery:3') return 'cutlery3pc';
  if (tag === 'service:cutlery:4') return 'tableSet';
  return 'unknown';
};
